import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { api, type ApiError, intercom, useUser } from 'libs';
import { Button } from 'components/common';
import { TeamSelectForm, type Team } from 'components/join-requests';

interface JoinRequestProps {
  nextStep: () => Promise<void>;
}

const JoinRequest = ({ nextStep }: JoinRequestProps) => {
  const user = useUser();
  const [showSelectTeam, setShowSelectTeam] = useState(false);
  const [availableTeams, setAvailableTeams] = useState<Team[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const teamName = user.brokerage?.name;
  const hasExistingBrokerage = Boolean(user.features.has_brokerage && teamName);

  const onSubmit = async (selectedTeam: Team) => {
    setIsLoading(true);
    try {
      await api.post('join-requests', { brokerage_id: selectedTeam.value });
      toast.success('Join request successfully sent', {
        toastId: 'toast-success-join-request-sent',
      });
      await nextStep();
    } catch (error) {
      toast.error(
        (error as ApiError)?.response?.data?.message || 'Join request could not be sent. Please try again later.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (hasExistingBrokerage) {
      return;
    }

    (async () => {
      const { data } = await api.get('brokerages');
      setAvailableTeams(data.map((team: { id: number; name: string }) => ({ label: team.name, value: team.id })));
    })();
  }, [hasExistingBrokerage]);

  if (hasExistingBrokerage) {
    return (
      <div className="p-5" data-testid="onboarding-team-join-request-step">
        <h5 className="fw-bold mb-4 text-center">Welcome to {teamName}</h5>
        <div className="mb-4">
          You have Platinum access through your team. Continue onboarding to finish setting up your Lender Spotlight
          account.
        </div>
        <div className="d-flex gap-2 flex-column">
          <Button
            onClick={nextStep}
            testId="onboarding-team-join-request-team-member-continue-button"
            text="bold"
            variant="primary-dark"
          >
            <div className="p-3">Continue onboarding</div>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-5" data-testid="onboarding-team-join-request-step">
      {showSelectTeam ? (
        <>
          <h5 className="fw-bold my-4 text-center">Select your team</h5>
          <TeamSelectForm
            availableTeams={availableTeams}
            cancelLabel="Back"
            disabled={isLoading}
            onCancel={() => setShowSelectTeam(false)}
            onSubmit={onSubmit}
            selectTestId="onboarding-team-join-request-select"
            submitTestId="onboarding-team-join-request-send-button"
            cancelTestId="onboarding-team-join-request-back-button"
          />
        </>
      ) : (
        <>
          <h5 className="fw-bold mb-4 text-center">Are you part of a brokerage team?</h5>
          <div className="mb-4">
            As a Platinum Team member, you&apos;ll get full access to Lender Spotlight, plus additional features and
            benefits available exclusively to your team.
            <br />
            If you can&apos;t find your team or have questions about joining, contact{' '}
            <Button
              onClick={() => intercom.showMessage("I'm new to Lender Spotlight and want to join a team.")}
              text="bold"
              variant="link-inline"
            >
              customer support
            </Button>
            .
          </div>
          <div className="d-flex gap-2 flex-column">
            <Button
              onClick={() => setShowSelectTeam(true)}
              testId="onboarding-team-join-request-open-button"
              text="bold"
              variant="border"
            >
              <div className="p-3">Yes, I&apos;m part of a team</div>
            </Button>
            <Button onClick={nextStep} testId="onboarding-team-join-request-skip-button" text="bold" variant="border">
              <div className="p-3">No, continue onboarding</div>
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default JoinRequest;
